import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MeetingProvider, useMeeting, useParticipant } from '@videosdk.live/react-sdk';
import { FaVideo, FaVideoSlash, FaMicrophone, FaMicrophoneSlash, FaPhoneSlash, FaSpinner, FaUserCircle } from 'react-icons/fa';

// Participant Tile with audio playback and spotlight support
function ParticipantView({ participantId, onSpotlight, isSpotlight }) {
  const { webcamStream, webcamOn, micOn, isLocal, displayName, micStream } = useParticipant(participantId);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (webcamOn && webcamStream && videoRef.current) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(webcamStream.track);
      videoRef.current.srcObject = mediaStream;
      videoRef.current.play().catch(() => {});
    }
  }, [webcamOn, webcamStream]);

  useEffect(() => {
    if (micOn && micStream && audioRef.current) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(micStream.track);
      audioRef.current.srcObject = mediaStream;
      audioRef.current.muted = isLocal; // avoid echo
      audioRef.current.play().catch(() => {});
    }
  }, [micOn, micStream, isLocal]);

  return (
    <div className={`relative rounded-lg overflow-hidden bg-gray-900 ${isSpotlight ? 'aspect-video col-span-2 row-span-2' : 'aspect-video'}`} onClick={() => onSpotlight && onSpotlight(participantId)}>
      {webcamOn ? (
        <video ref={videoRef} className="w-full h-full object-cover" muted={isLocal} playsInline />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-400">
          <FaUserCircle className="text-5xl" />
        </div>
      )}
      <audio ref={audioRef} autoPlay playsInline />
      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
        {displayName || (isLocal ? 'You' : 'Guest')}{!micOn && ' · Muted'}
      </div>
    </div>
  );
}

// Controls Component
function Controls({ joined, onFullscreen }) {
  const { toggleMic, toggleWebcam, leave, localMicOn, localWebcamOn } = useMeeting();
  const [isLeaving, setIsLeaving] = useState(false);

  return (
    <div className="flex flex-wrap justify-center gap-4 p-4 bg-gray-100 rounded-lg m-4">
      <button
        onClick={() => toggleMic()}
        disabled={!joined || isLeaving}
        className={`px-4 py-2 rounded flex items-center gap-2 ${
          !localMicOn ? 'bg-red-500' : 'bg-blue-500'
        } text-white ${(!joined || isLeaving) ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        {!localMicOn ? <FaMicrophoneSlash /> : <FaMicrophone />}
        {!localMicOn ? 'Unmute' : 'Mute'}
      </button>
      <button
        onClick={() => toggleWebcam()}
        disabled={!joined || isLeaving}
        className={`px-4 py-2 rounded flex items-center gap-2 ${
          !localWebcamOn ? 'bg-red-500' : 'bg-blue-500'
        } text-white ${(!joined || isLeaving) ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        {localWebcamOn ? <FaVideo /> : <FaVideoSlash />}
        {localWebcamOn ? 'Stop Video' : 'Start Video'}
      </button>
      <button
        onClick={async () => {
          try {
            if (isLeaving || !joined) return;
            setIsLeaving(true);
            await leave();
          } catch (e) {
            console.error('Leave failed', e);
          }
        }}
        disabled={!joined || isLeaving}
        className={`px-4 py-2 rounded text-white flex items-center gap-2 ${(joined && !isLeaving) ? 'bg-red-500' : 'bg-gray-400 cursor-not-allowed'}`}
      >
        <FaPhoneSlash /> Leave
      </button>
      <button
        onClick={onFullscreen}
        className="px-4 py-2 rounded bg-gray-700 text-white"
        title="Fullscreen"
      >
        Fullscreen
      </button>
    </div>
  );
}

// Meeting View Component
function MeetingView({ token, meetingId, onMeetingLeave }) {
  const [joined, setJoined] = useState(false);
  const [participants, setParticipants] = useState([]);
  const meetingRef = useRef();
  const containerRef = useRef(null);
  const [spotlightId, setSpotlightId] = useState(null);
  const [activity, setActivity] = useState([]); // basic transcript/activity placeholder
  const [participantNames, setParticipantNames] = useState({});

  const { join, participants: meetingParticipants, toggleMic, toggleWebcam, localMicOn, localWebcamOn } = useMeeting({
    onMeetingJoined: () => {
      setJoined(true);
    },
    onMeetingLeft: () => {
      // Only notify parent after actual leave to avoid race conditions
      if (onMeetingLeave) onMeetingLeave();
    },
    onParticipantJoined: (participant) => {
      setParticipants((prev) => [...prev, participant.id]);
      setActivity((a) => [{ t: Date.now(), m: `${participant.displayName || 'Guest'} joined` }, ...a]);
      setParticipantNames((m) => ({ ...m, [participant.id]: participant.displayName || participant.id.slice(0,6) }));
    },
    onParticipantLeft: (participant) => {
      setParticipants((prev) => prev.filter((id) => id !== participant.id));
      setActivity((a) => [{ t: Date.now(), m: `${participant.displayName || 'Guest'} left` }, ...a]);
      setParticipantNames((m) => {
        const c = { ...m };
        delete c[participant.id];
        return c;
      });
    },
  });

  useEffect(() => {
    if (meetingRef.current) return;
    meetingRef.current = true;
    join();
  }, [join]);

  useEffect(() => {
    if (meetingParticipants) {
      const ids = Array.from(meetingParticipants.keys());
      setParticipants(ids);
      // best effort set names if available
      const names = {};
      ids.forEach((id) => {
        const p = meetingParticipants.get(id);
        if (p && p.displayName) names[id] = p.displayName;
      });
      if (Object.keys(names).length) {
        setParticipantNames((m) => ({ ...names, ...m }));
      }
    }
  }, [meetingParticipants]);

  // Keyboard shortcuts: M (mute/unmute), V (video on/off), F (fullscreen)
  useEffect(() => {
    const onKey = (e) => {
      const key = e.key.toLowerCase();
      if (key === 'm') {
        e.preventDefault();
        toggleMic();
      } else if (key === 'v') {
        e.preventDefault();
        toggleWebcam();
      } else if (key === 'f') {
        e.preventDefault();
        requestFullscreen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleMic, toggleWebcam]);

  const requestFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      el.requestFullscreen && el.requestFullscreen();
    }
  };

  const onSpotlight = (pid) => setSpotlightId((cur) => (cur === pid ? null : pid));

  const sorted = spotlightId
    ? [spotlightId, ...participants.filter((p) => p !== spotlightId)]
    : participants;

  // Auto-enable mic/webcam when 2+ participants
  useEffect(() => {
    if (joined && participants.length >= 2) {
      if (!localMicOn) toggleMic();
      if (!localWebcamOn) toggleWebcam();
    }
  }, [joined, participants.length, toggleMic, toggleWebcam, localMicOn, localWebcamOn]);

  const haveTwoParticipants = participants.length >= 2;

  return (
    <div ref={containerRef} className="flex h-[70vh] rounded-lg overflow-hidden bg-white border relative">
      {/* Left: Participants list */}
      <aside className="hidden md:flex w-56 flex-col border-r bg-gray-50 p-3 gap-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <span>Participants</span>
            <span className="inline-flex items-center justify-center text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700">{participants.length}</span>
          </div>
          {/* Compact share icons */}
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                const baseUrl = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
                const shareUrl = `${baseUrl}/meetings/${encodeURIComponent(meetingId)}`;
                try { await navigator.clipboard.writeText(shareUrl); } catch(e) {}
              }}
              className="w-7 h-7 rounded bg-blue-600 text-white text-xs flex items-center justify-center"
              title="Copy link"
            >
              ⧉
            </button>
            <button
              onClick={async () => {
                const baseUrl = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
                const shareUrl = `${baseUrl}/meetings/${encodeURIComponent(meetingId)}`;
                if (navigator.share) {
                  try { await navigator.share({ title: 'Meeting', url: shareUrl }); } catch(e) {}
                } else {
                  try { await navigator.clipboard.writeText(shareUrl); } catch(e) {}
                }
              }}
              className="w-7 h-7 rounded bg-green-600 text-white text-xs flex items-center justify-center"
              title="Share"
            >
              ↗
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto space-y-2">
          {participants.length === 0 && <div className="text-xs text-gray-500">Waiting for participants…</div>}
          {participants.map((pid) => {
            const name = participantNames[pid] || pid.slice(0, 6);
            return (
              <div key={pid} className={`text-sm px-2 py-1 rounded cursor-pointer ${spotlightId === pid ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`} onClick={() => onSpotlight(pid)}>
                {name} {spotlightId === pid && '• spotlight'}
              </div>
            );
          })}
        </div>
      </aside>

      {/* Center: Video grid */}
      <main className="flex-1 flex flex-col">
        <div className="flex-1 overflow-auto p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 auto-rows-[1fr] gap-3">
          {joined && sorted.map((pid) => (
            <ParticipantView key={pid} participantId={pid} onSpotlight={onSpotlight} isSpotlight={spotlightId === pid} />
          ))}
        </div>
        {joined && participants.length < 2 && (
          <div className="mx-3 mb-2 rounded bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs px-3 py-2">
            <div>Waiting for your co‑participant to join. You can test your mic and camera meanwhile.</div>
            <div className="mt-1 text-[11px] text-yellow-900/90">
              Mic: <span className="font-semibold">{localMicOn ? 'On' : 'Off'}</span> · Camera: <span className="font-semibold">{localWebcamOn ? 'On' : 'Off'}</span>
            </div>
          </div>
        )}
        <Controls joined={joined} onFullscreen={requestFullscreen} />
      </main>

      {/* Right: Transcript / Activity */}
      <aside className="hidden lg:flex w-72 flex-col border-l bg-gray-50 p-3">
        <div className="text-sm font-semibold text-gray-700 mb-2">Live Transcript</div>
        <div className="flex-1 overflow-auto space-y-2 text-sm">
          {activity.length === 0 && <div className="text-xs text-gray-500">No transcript yet. Coming soon.</div>}
          {activity.map((e, idx) => (
            <div key={idx} className="bg-white rounded border px-2 py-1 text-gray-700">
              {new Date(e.t).toLocaleTimeString()} · {e.m}
            </div>
          ))}
        </div>
      </aside>

      {/* Waiting overlay until 2 participants */}
      {joined && participants.length < 2 && (
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-lg shadow px-4 py-3 text-sm text-gray-700">
            Waiting for another participant to join…
          </div>
        </div>
      )}
    </div>
  );
}

// Main Component
const VideoSDKWrapper = ({ meetingId: initialMeetingId, onMeetingLeave, participantName: participantNameProp }) => {
  const [meetingId, setMeetingId] = useState(initialMeetingId);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [participantName] = useState(participantNameProp || 'Guest');
  const isCreatingRef = useRef(false);
  const isGeneratingRef = useRef(false);

  // Resolve backend URLs from env (with fallbacks)
  // Use our Cloud Functions for both creating meeting and generating token so both share the same VideoSDK credentials
  const tokenUrl = process.env.REACT_APP_GENERATE_TOKEN_URL || 'https://us-central1-asdflobeta.cloudfunctions.net/generateVideoToken';
  const createUrl = process.env.REACT_APP_CREATE_MEETING_URL || 'https://us-central1-asdflobeta.cloudfunctions.net/createVideoMeetingHttp';

  // Function to generate a token for the meeting
  const generateToken = useCallback(async (roomId) => {
    try {
      if (isGeneratingRef.current) return;
      isGeneratingRef.current = true;
      setLoading(true);
      setError(null);
      console.log('[VideoSDK] generateToken for roomId:', roomId);
      
      const response = await fetch(tokenUrl, {
        method: 'POST',
        mode: 'cors',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          roomId, 
          participantName,
          timestamp: Date.now() // Add timestamp to prevent caching
        })
      });
      
      const data = await response.json();
      console.log('[VideoSDK] token response ok?', response.ok, 'token prefix:', (data && data.token ? data.token.slice(0, 12) : 'none'));
      
      if (!response.ok) {
        const detail = data && (data.error || data.details) ? ` (${data.error || data.details})` : '';
        throw new Error(`Failed to generate token${detail}`);
      }
      
      if (data.token) {
        setToken(data.token);
      } else {
        throw new Error('No token returned from server');
      }
    } catch (err) {
      console.error('Error generating token:', err);
      setError(err.message || 'Failed to join meeting. Please try again.');
    } finally {
      setLoading(false);
      isGeneratingRef.current = false;
    }
  }, [participantName, tokenUrl]);

  // Function to create a new meeting
  const createMeeting = useCallback(async () => {
    if (isCreatingRef.current) return;
    isCreatingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      // If we already have a meeting ID, just generate a token
      if (meetingId) {
        console.log('[VideoSDK] existing meetingId, generating token:', meetingId);
        await generateToken(meetingId);
        return;
      }

      // Create a new meeting and get the room ID
      const response = await fetch(createUrl, {
        method: 'POST',
        mode: 'cors',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          participantName,
          timestamp: Date.now() // Add timestamp to prevent caching
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to create meeting');
      }
      
      if (data.roomId) {
        console.log('[VideoSDK] created meetingId:', data.roomId);
        setMeetingId(data.roomId);
        await generateToken(data.roomId);
      } else {
        throw new Error('No room ID returned from server');
      }
    } catch (err) {
      console.error('Error creating meeting:', err);
      setError(err.message || 'Failed to create meeting. Please try again.');
      setLoading(false);
    } finally {
      isCreatingRef.current = false;
    }
  }, [generateToken, meetingId, participantName, createUrl]);

  // Handle meeting leave
  const handleMeetingLeave = useCallback(() => {
    setToken(null);
    if (onMeetingLeave) onMeetingLeave();
  }, [onMeetingLeave]);

  // Initialize meeting when component mounts
  useEffect(() => {
    if (!token && !loading) {
      createMeeting();
    }
  }, [token, loading, createMeeting]);

  // Show loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <FaSpinner className="animate-spin text-4xl text-blue-500 mb-4" />
        <p>Setting up your meeting...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        <p className="font-bold">Error</p>
        <p>{error}</p>
        <button
          onClick={createMeeting}
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Show meeting view when token is available
  if (token && meetingId) {
    console.log('[VideoSDK] rendering MeetingProvider with meetingId:', meetingId, 'token prefix:', token.slice(0, 12));

    return (
      <MeetingProvider
        config={{
          meetingId,
          micEnabled: true,
          webcamEnabled: true,
          name: participantName,
          multiStream: true,
        }}
        token={token}
      >
        <MeetingView 
          token={token} 
          meetingId={meetingId} 
          onMeetingLeave={handleMeetingLeave} 
        />
      </MeetingProvider>
    );
  }

  // Default state (shouldn't be visible, but just in case)
  return (
    <div className="flex flex-col items-center justify-center h-64">
      <FaSpinner className="animate-spin text-4xl text-blue-500 mb-4" />
      <p>Preparing your meeting...</p>
    </div>
  );
};

export default VideoSDKWrapper;
